/**
 * Decides whether the login screen should start Entra SSO on its own.
 *
 * Entra can normally complete sign-in without interaction, so showing a
 * "Sign in with Microsoft" button as the only path adds a pointless click.
 * This attempts the redirect automatically and leaves the button as the
 * fallback for when SSO genuinely cannot complete silently.
 *
 * The hazard is a redirect loop (app -> Entra -> app -> Entra ...), so the
 * attempt is gated hard: only after the silent restore has finished, only
 * when unauthenticated, never with an error on screen, and at most once per
 * browser session. The attempt flag lives in sessionStorage because the
 * redirect reloads the page and wipes component state.
 */

export const AUTO_LOGIN_ATTEMPTED_KEY = 'squid.autoLoginAttempted';
const EXPLICIT_SIGN_OUT_KEY = 'squid.explicitSignOut';

/** True when the login screen should trigger SSO without user input. */
export const shouldAutoLogin = ({
  loading,
  isAuthenticated,
  error,
  devMode,
  skipAuth,
  alreadyAttempted,
  explicitSignOut,
}) => {
  // Mid-restore: a redirect now would abort a session about to succeed.
  if (loading) return false;
  if (isAuthenticated) return false;
  // Leave failures on screen so they can be read and acted on.
  if (error) return false;
  if (devMode || skipAuth) return false;
  // One attempt per session — the loop guard.
  if (alreadyAttempted) return false;
  // After an explicit sign-out, the user wants to stay signed out.
  if (explicitSignOut) return false;
  return true;
};

/** Reads the once-per-session attempt flag. */
export const hasAttemptedAutoLogin = () => {
  try {
    return sessionStorage.getItem(AUTO_LOGIN_ATTEMPTED_KEY) === 'true';
  } catch {
    // Hardened browsers can block storage; treat as "already attempted" so we
    // fall back to the button rather than risking a loop.
    return true;
  }
};

/** Marks the attempt so the Entra round trip cannot restart it. */
export const markAutoLoginAttempted = () => {
  try {
    sessionStorage.setItem(AUTO_LOGIN_ATTEMPTED_KEY, 'true');
  } catch {
    /* storage unavailable — the button remains available */
  }
};

/** Clears the flag, so a later deliberate sign-in can auto-run again. */
export const clearAutoLoginAttempt = () => {
  try {
    sessionStorage.removeItem(AUTO_LOGIN_ATTEMPTED_KEY);
  } catch {
    /* nothing to do */
  }
};

/** Records that the user signed out on purpose. */
export const markExplicitSignOut = () => {
  try {
    sessionStorage.setItem(EXPLICIT_SIGN_OUT_KEY, 'true');
  } catch {
    /* storage unavailable */
  }
};

/** True when the last navigation to /login came from a deliberate sign-out. */
export const hasExplicitlySignedOut = () => {
  try {
    return sessionStorage.getItem(EXPLICIT_SIGN_OUT_KEY) === 'true';
  } catch {
    return false;
  }
};

/** Clears the sign-out marker once the user chooses to sign in again. */
export const clearExplicitSignOut = () => {
  try {
    sessionStorage.removeItem(EXPLICIT_SIGN_OUT_KEY);
  } catch {
    /* nothing to do */
  }
};
